import { isEmpty, isNil } from 'lodash';
import { getReservationsByProductType } from '.';

/*
  If just one ferry -> simple
  If more than two ferries -> complex
  If two ferries, one to island and one from island -> simple
     A -> B, C -> A or A -> B, B -> C
*/
export const isComplexBookingFromQT = (booking) => {
  const ferryReservations = getReservationsByProductType('ferry', booking);
  if (ferryReservations.length <= 1) {
    return false;
  }
  if (ferryReservations.length > 2) {
    return true;
  }

  const [departure, destination] = ferryReservations;
  const {
    from_route_stop_attributes: { stop_id: departureBegin },
    to_route_stop_attributes: { stop_id: departureEnd },
  } = departure;
  const {
    from_route_stop_attributes: { stop_id: destinationBegin },
    to_route_stop_attributes: { stop_id: destinationEnd },
  } = destination;

  return departureBegin !== destinationEnd && departureEnd !== destinationBegin;
};

export const isSimpleBooking = (
  booking,
  allowedVehicleTypes,
  fullyEdit = false
) => {
  const { vehicles_attributes = [], reservations_attributes } = booking;

  const notAllowedVehicleType = vehicles_attributes.find(
    (vehicle) => !allowedVehicleTypes.includes(vehicle.vehicle_type_id)
  );
  if (notAllowedVehicleType) {
    return false;
  }

  if (fullyEdit) {
    const ferryReservations = getReservationsByProductType('ferry', booking);

    // If there are no ferry reservations or more than 2 ferry reservations,
    // Or the booking is from QT console where there is no definition of departure and return in cms
    // Then the booking is not simple
    if (
      ferryReservations.length < 1 ||
      ferryReservations.length > 2 ||
      isComplexBookingFromQT(booking)
    ) {
      return false;
    }

    const { optionalExtras = [] } =
      ecom.craftConfig?.bookingPreferences?.optionalExtras || {};
    const optionalExtraResourceIds = optionalExtras
      .map((item) => item.qtResourceId)
      .filter((item) => !isNil(item));
    const optionalExtraRouteIds = optionalExtras
      .map((item) => item.routeIdOfCoach)
      .filter((item) => !isNil(item));
    const optionalExtraReservations = reservations_attributes.filter(
      ({ resource_id, route_id }) =>
        optionalExtraResourceIds.includes(resource_id) ||
        optionalExtraRouteIds.includes(route_id)
    );
    const transferReservations = getReservationsByProductType(
      'transfer',
      booking
    ).filter(
      ({ route_id }) => route_id && !optionalExtraRouteIds.includes(route_id)
    );

    // If there are reservations other than ferry, transfer and optional extras reservations,
    // Then the booking is not simple
    if (
      ferryReservations.length +
        transferReservations.length +
        optionalExtraReservations.length !==
      reservations_attributes.length
    ) {
      return false;
    }
  } else if (
    /*
     The booking is NOT 'Simple' when
     1, There is reservation other than ferry
     2, Reservation count is more than 2
     3, It is the kind of booking from QT console where
        there is no definition of departure and return
   */
    isEmpty(reservations_attributes) ||
    reservations_attributes.length > 2 ||
    getReservationsByProductType('ferry', booking).length !==
      reservations_attributes.length ||
    isComplexBookingFromQT(booking)
  ) {
    return false;
  }

  return true;
};

// Freight bookings (vehicle type is a freight unit, horse float, etc.) cannot
// be cancelled online — cancellation must be handled by phone/offline. Online
// modification is not gated by this helper. The list of freight QT vehicle
// type ids comes from the CMS vehicleConfig global set.
export const bookingHasFreightVehicle = (booking, freightVehicleTypeIds) => {
  if (isEmpty(freightVehicleTypeIds)) {
    return false;
  }
  const { vehicles_attributes: vehicles = [] } = booking || {};
  return vehicles.some((vehicle) =>
    freightVehicleTypeIds.includes(vehicle.vehicle_type_id)
  );
};

export const hasReservation = (booking) =>
  !isEmpty(booking) &&
  booking.reservations_attributes &&
  booking.reservations_attributes.length !== 0;

export const receiptPagePath = (bookingId) =>
  `/bookings/checkout/receipt/${bookingId}`;

export const isBookingFromSysAdmin = (booking) =>
  booking?.creator?.name === 'Sys Admin';

export const isOptionExtraEnabled = () =>
  Boolean(
    ecom.craftConfig.bookingPreferences?.optionalExtras?.optionalExtras?.length
  );

export const isKICarParkingEnabled = () =>
  ecom.craftConfig.site === 'kangarooIsland' && isOptionExtraEnabled();

// Check if all passengers are unchecked in a reservation
export const isReservationUnchecked = (reservation) =>
  Boolean(
    reservation.passenger_splits?.every(
      (passenger) => passenger.check_in_state === 'unchecked'
    )
  );
